/**
 * @voltxampere/presets — LED current limiter (#4)
 *
 * V1 → R → LED → gnd. The series resistor limits forward current.
 * With V1=5V and Rseries=330Ω, the diode equation Vd = nVt·ln(I/Is)
 * settles around 0.7 V for the engine's default Shockley
 * parameters (Is=1e-14 A); the resistor drop is V1−Vd ≈ 4.3 V, so
 * I_LED ≈ 13 mA. Real LEDs have Vf ≈ 1.7-2.0 V — the SPICE
 * default-diode model under-estimates Vf because it lacks the
 * built-in voltage offset, so Sprint 22+ will swap in an LED-
 * specific stamp. For now the test pins the engine's actual
 * computed Vd and we acknowledge the model gap in the learning
 * goal.
 *
 * Sprint 25 R2 lane 4D-A — hand-authored layout. V1 vertical at
 * left, R1 horizontal across the top rail, LED vertical at right
 * dropping `anode` net to `gnd`. Body centers rounded to 20-px
 * grid; pin world coords derived from position + rotation.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { makeCircuit, makeNet, makeComponent } from "../_builders.js";
export const ledCurrentLimiter = {
    id: "led-current-limiter",
    name: { tr: "LED Akım Sınırlayıcı", en: "LED Current Limiter" },
    category: "basic",
    difficulty: 1,
    description: {
        tr: "Seri direnç ile LED akım sınırlama. V1=5V, R=330Ω, LED → I≈13mA.",
        en: "Series resistor limits LED current. V1=5V, R=330Ω, LED → I≈13mA.",
    },
    circuit: makeCircuit({
        slot: 4,
        name: "LED Current Limiter",
        description: "Series resistor LED current limiter",
        nets: [
            makeNet({ name: "gnd", isGround: true }),
            makeNet({ name: "vcc" }),
            makeNet({ name: "anode" }),
        ],
        components: [
            makeComponent({
                id: "V1",
                type: "voltage-source",
                params: { voltage: 5 },
                terminals: { pos: "net_vcc", neg: "net_gnd" },
                label: "V1",
                position: { x: -200, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "R1",
                type: "resistor",
                params: { resistance: 330 },
                terminals: { p1: "net_vcc", p2: "net_anode" },
                label: "R1",
                position: { x: -60, y: -80 },
                rotation: 0,
            }),
            makeComponent({
                // Sprint 78 fix — fixture was authored as `type: "diode"` with
                // silicon Is=1e-14, which gives V_F ≈ 0.72 V (correct for
                // signal-Si diode, wrong for red LED). The `led` component
                // ships Shockley defaults tuned for ~1.82 V V_F at the
                // 5 V / 330 Ω operating point (Is=1e-12, n=3 — see
                // packages/engine/src/stamps/nonlinear/led.ts). Kol E Tier-2
                // T2-1 analytical = 1.8 V now matches.
                id: "D1",
                type: "led",
                params: {},
                terminals: { anode: "net_anode", cathode: "net_gnd" },
                label: "LED1",
                position: { x: 60, y: 0 },
                rotation: 90,
            }),
            makeComponent({
                id: "GND1",
                type: "ground",
                params: {},
                terminals: { p: "net_gnd" },
                position: { x: -200, y: 80 },
                rotation: 0,
            }),
        ],
        wires: [
            // vcc rail: V1.pos (-200, -10) → R1.p1 (-80, -80)
            {
                id: "w_vcc",
                netId: "net_vcc",
                path: [
                    { x: -200, y: -10 },
                    { x: -200, y: -80 },
                    { x: -80, y: -80 },
                ],
                style: "orthogonal",
            },
            // anode rail: R1.p2 (-40, -80) → D1.A (60, -20)
            {
                id: "w_anode",
                netId: "net_anode",
                path: [
                    { x: -40, y: -80 },
                    { x: 60, y: -80 },
                    { x: 60, y: -20 },
                ],
                style: "orthogonal",
            },
            // gnd rail leg A: V1.neg (-200, 10) → GND.p (-200, 71)
            {
                id: "w_gnd_left",
                netId: "net_gnd",
                path: [
                    { x: -200, y: 10 },
                    { x: -200, y: 71 },
                ],
                style: "orthogonal",
            },
            // gnd rail leg B: D1.K (60, 20) → GND.p (-200, 71)
            {
                id: "w_gnd_right",
                netId: "net_gnd",
                path: [
                    { x: 60, y: 20 },
                    { x: 60, y: 110 },
                    { x: -200, y: 110 },
                    { x: -200, y: 71 },
                ],
                style: "orthogonal",
            },
        ],
    }),
    expectedValues: [
        // Sprint 78 fix (ADR-0113): fixture flipped `type: "diode"` (Si,
        // V_F ≈ 0.69 V) to `type: "led"` (red-LED wrapper Is=1e-12 n=3,
        // V_F ≈ 1.78 V). The expected value follows. ±0.10 band
        // accommodates Newton reltol=1e-3 + LED defaults headroom.
        {
            node: "anode",
            analysis: "dc",
            value: 1.78,
            tolerance: 0.1,
            unit: "V",
        },
    ],
    learningGoals: [
        {
            tr: "Doğrusal olmayan diyot eğrisinde çalışma noktası: Shockley denklemi.",
            en: "Operating point on the nonlinear diode curve: Shockley equation.",
        },
        {
            tr: "Seri direnç ile akım sınırlama prensibi.",
            en: "Current limiting via a series resistor.",
        },
        {
            tr: "Not: gerçek LED için Vf ≈ 1.7-2.0 V — Sprint 22+ LED stamp'i ileri gerilim ofsetini ekleyecek.",
            en: "Note: real LED Vf ≈ 1.7-2.0 V — Sprint 22+ LED stamp will add the forward offset.",
        },
    ],
    references: ["Horowitz/Hill, The Art of Electronics, Ch. 1"],
};
//# sourceMappingURL=led-current-limiter.js.map